/**
 * CareLogix™ ORACLE — AdminOne CEO Membership Control™ (additive).
 *
 * RBAC enforced on the BACKEND. Frontend is decision-support UX only.
 * Truthfulness: features tagged FEATURE_AWAITING_INTEGRATION are shown
 * honestly — never as "live" or "active".
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { getAdminToken } from "@/api/adminOneClient";

const BACKEND =
  (typeof process !== "undefined" && process.env && process.env.REACT_APP_BACKEND_URL) ||
  "";

async function _req(path, opts = {}) {
  const tok = getAdminToken();
  if (!tok) throw new Error("AdminOne token missing — please sign in.");
  const res = await fetch(`${BACKEND}${path}`, {
    ...opts,
    headers: {
      "Content-Type": "application/json",
      ...(opts.headers || {}),
      Authorization: `Bearer ${tok}`,
    },
  });
  const text = await res.text();
  let json = null;
  try { json = text ? JSON.parse(text) : null; } catch { json = { detail: text }; }
  if (!res.ok) throw new Error((json && (json.detail || json.error)) || `HTTP ${res.status}`);
  return json;
}

const TIERS = ["GUEST", "PROFESSIONAL", "EXECUTIVE", "ENTERPRISE"];

const TIER_COLOR = {
  GUEST:        "#94a3b8",
  PROFESSIONAL: "#7AC860",
  EXECUTIVE:    "#fcd34d",
  ENTERPRISE:   "#c5a059",
};

const STATUS_COLOR = {
  ACTIVE:    "#7AC860",
  TRIAL:     "#fcd34d",
  COMPED:    "#c5a059",
  REVOKED:   "#ef4444",
  SUSPENDED: "#ef4444",
};

const S = {
  page: { padding: "24px 28px 80px", background: "linear-gradient(160deg, #050b09 0%, #06231b 60%, #050b09 100%)", color: "#dfe3eb", minHeight: "100vh", fontFamily: "'Figtree', 'Inter', system-ui, sans-serif" },
  header: { display: "flex", justifyContent: "space-between", alignItems: "flex-end", gap: 16, flexWrap: "wrap", marginBottom: 18 },
  eyebrow: { color: "#c5a059", fontFamily: "'Space Mono', monospace", fontSize: 11, letterSpacing: "0.18em", marginBottom: 4 },
  title: { color: "#fde68a", margin: 0, fontSize: 26, fontFamily: "'Cinzel', serif", letterSpacing: "0.02em" },
  sub: { color: "#cbd5e1", marginTop: 8, fontSize: 12.5, maxWidth: 820, lineHeight: 1.5 },
  grid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 12, marginBottom: 18 },
  kpi: { background: "rgba(13,26,15,0.92)", border: "1px solid rgba(245,200,66,0.18)", borderRadius: 12, padding: "12px 14px" },
  kpiLabel: { color: "#c5a059", fontFamily: "'Space Mono', monospace", fontSize: 10, letterSpacing: "0.14em" },
  kpiValue: { color: "#fde68a", fontFamily: "'Cinzel', serif", fontSize: 24, marginTop: 6 },
  kpiSub: { color: "#94a3b8", fontSize: 11, marginTop: 4 },
  section: { background: "rgba(13,26,15,0.65)", border: "1px solid rgba(245,200,66,0.20)", borderRadius: 14, padding: 16, marginBottom: 16 },
  sectionTitle: { color: "#fde68a", fontFamily: "'Cinzel', serif", fontSize: 16, margin: 0, marginBottom: 8 },
  toolbar: { display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 10 },
  input: { background: "#040608", border: "1px solid rgba(255,255,255,0.10)", color: "#e2e8f0", padding: "8px 11px", borderRadius: 8, fontSize: 12, minWidth: 200 },
  select: { background: "#040608", border: "1px solid rgba(255,255,255,0.10)", color: "#e2e8f0", padding: "8px 11px", borderRadius: 8, fontSize: 12 },
  btn: { appearance: "none", background: "linear-gradient(135deg, #c5a059, #ffd966)", border: "1px solid #c5a059", color: "#0c1a10", padding: "8px 13px", borderRadius: 8, cursor: "pointer", fontFamily: "'Space Mono', monospace", fontSize: 11, fontWeight: 800, letterSpacing: "0.06em" },
  btnGhost: { appearance: "none", background: "transparent", border: "1px solid rgba(255,255,255,0.20)", color: "#cbd5e1", padding: "7px 11px", borderRadius: 8, cursor: "pointer", fontFamily: "'Space Mono', monospace", fontSize: 11 },
  pill: { fontFamily: "'Space Mono', monospace", fontSize: 10, fontWeight: 800, letterSpacing: "0.10em", padding: "3px 9px", border: "1px solid", borderRadius: 999, display: "inline-block" },
  table: { width: "100%", borderCollapse: "collapse", fontSize: 12 },
  th: { textAlign: "left", color: "#c5a059", fontFamily: "'Space Mono', monospace", fontSize: 10, letterSpacing: "0.12em", padding: "8px 8px", borderBottom: "1px solid rgba(245,200,66,0.18)" },
  td: { padding: "9px 8px", borderBottom: "1px solid rgba(255,255,255,0.05)", color: "#cbd5e1", verticalAlign: "middle" },
  rowActions: { display: "flex", gap: 6, flexWrap: "wrap" },
  modalBack: { position: "fixed", inset: 0, background: "rgba(2,6,4,0.78)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 50, padding: 18 },
  modal: { background: "#0a1610", border: "1px solid rgba(245,200,66,0.30)", borderRadius: 14, padding: 22, width: "min(520px, 92vw)", maxHeight: "88vh", overflow: "auto" },
  modalTitle: { color: "#fde68a", fontFamily: "'Cinzel', serif", fontSize: 18, marginBottom: 6 },
  field: { display: "flex", flexDirection: "column", marginBottom: 10 },
  label: { color: "#c5a059", fontFamily: "'Space Mono', monospace", fontSize: 10, letterSpacing: "0.10em", marginBottom: 4 },
  toast: { position: "fixed", bottom: 24, right: 24, background: "rgba(13,26,15,0.96)", border: "1px solid rgba(245,200,66,0.40)", borderRadius: 10, padding: "10px 14px", color: "#fde68a", fontSize: 12, zIndex: 60 },
  compliance: { marginTop: 20, padding: "12px 14px", background: "rgba(252,211,77,0.06)", border: "1px solid rgba(252,211,77,0.30)", borderRadius: 12, color: "#fde68a", fontSize: 12 },
};

function Pill({ children, color }) {
  return <span style={{ ...S.pill, color, borderColor: color, background: `${color}1a` }}>{children}</span>;
}

function fmtDate(s) {
  if (!s) return "—";
  try { return new Date(s).toLocaleString(); } catch { return s; }
}

// ---------- Sub-components ----------

function MembershipTierSelector({ value, onChange, testid = "tier-selector" }) {
  return (
    <select style={S.select} value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}>
      {TIERS.map((t) => <option key={t} value={t}>{t}</option>)}
    </select>
  );
}

function MembershipAssignmentModal({ open, kind, user, onClose, onSubmit, busy }) {
  const [tier, setTier]       = useState(user?.current_tier || "PROFESSIONAL");
  const [reason, setReason]   = useState("");
  const [expires, setExpires] = useState("");

  useEffect(() => {
    if (open) {
      setTier(user?.current_tier || "PROFESSIONAL");
      setReason("");
      setExpires("");
    }
  }, [open, user]);

  if (!open) return null;
  const titles = {
    assign:    "Assign tier",
    upgrade:   "Upgrade tier",
    downgrade: "Downgrade tier",
    revoke:    "Revoke access",
    comp:      "Grant comped access",
    trial:     "Grant trial",
  };
  return (
    <div style={S.modalBack} data-testid="membership-modal">
      <div style={S.modal}>
        <div style={S.modalTitle}>{titles[kind] || "Membership change"}</div>
        <p style={{ color: "#cbd5e1", fontSize: 12, marginBottom: 12 }}>
          Target: <strong>{user?.email || user?.user_id || "—"}</strong> · Current: <Pill color={TIER_COLOR[user?.current_tier] || "#94a3b8"}>{user?.current_tier || "GUEST"}</Pill>
        </p>

        {kind !== "revoke" && (
          <div style={S.field}>
            <span style={S.label}>New tier</span>
            <MembershipTierSelector value={tier} onChange={setTier} testid="modal-tier-selector" />
          </div>
        )}

        <div style={S.field}>
          <span style={S.label}>Reason (required, min 3 chars)</span>
          <input style={S.input} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="CEO approved access" data-testid="modal-reason" />
        </div>

        {kind === "trial" && (
          <div style={S.field}>
            <span style={S.label}>Trial days (default 14)</span>
            <input type="number" min="1" max="365" style={S.input} value={expires || "14"} onChange={(e) => setExpires(e.target.value)} data-testid="modal-trial-days" />
          </div>
        )}
        {(kind === "assign" || kind === "upgrade" || kind === "downgrade" || kind === "comp") && (
          <div style={S.field}>
            <span style={S.label}>Expires at (ISO 8601, optional)</span>
            <input style={S.input} value={expires} onChange={(e) => setExpires(e.target.value)} placeholder="" data-testid="modal-expires" />
          </div>
        )}

        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 14 }}>
          <button type="button" style={S.btnGhost} onClick={onClose} disabled={busy} data-testid="modal-cancel">Cancel</button>
          <button
            type="button"
            style={S.btn}
            disabled={busy || (reason || "").trim().length < 3 || (kind !== "revoke" && !tier)}
            onClick={() => onSubmit({ tier, reason: reason.trim(), expires_at: expires || null, trial_days: kind === "trial" ? Number(expires || 14) : undefined })}
            data-testid="modal-submit"
          >
            {busy ? "Saving…" : (titles[kind] || "Submit")}
          </button>
        </div>
      </div>
    </div>
  );
}

function MembershipEntitlementViewer({ entitlements }) {
  if (!entitlements || !entitlements.length) {
    return <p style={{ color: "#94a3b8", fontSize: 12 }}>NO_DATA</p>;
  }
  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 8 }} data-testid="entitlement-viewer">
      {entitlements.map((e) => (
        <div key={e.key} style={{ background: "rgba(13,26,15,0.92)", border: "1px solid rgba(245,200,66,0.18)", borderRadius: 10, padding: "10px 12px" }} data-testid={`entitlement-${e.key}`}>
          <div style={{ color: "#fde68a", fontFamily: "'Cinzel', serif", fontSize: 13, marginBottom: 4 }}>{e.label}</div>
          <Pill color={e.status === "LIVE" ? "#7AC860" : "#fcd34d"}>{e.status}</Pill>
          <span style={{ color: "#94a3b8", fontFamily: "'Space Mono', monospace", fontSize: 10, marginLeft: 6 }}>{e.tier}</span>
        </div>
      ))}
    </div>
  );
}

function MembershipAuditPanel({ events }) {
  if (!events || !events.length) {
    return <p style={{ color: "#fcd34d", fontFamily: "'Space Mono', monospace", fontSize: 11 }} data-testid="audit-empty">NO_DATA_AVAILABLE</p>;
  }
  return (
    <table style={S.table} data-testid="audit-table">
      <thead>
        <tr>
          <th style={S.th}>EVENT</th>
          <th style={S.th}>ACTOR</th>
          <th style={S.th}>TARGET</th>
          <th style={S.th}>OLD → NEW</th>
          <th style={S.th}>REASON</th>
          <th style={S.th}>WHEN</th>
        </tr>
      </thead>
      <tbody>
        {events.map((e, i) => (
          <tr key={i} data-testid={`audit-row-${i}`}>
            <td style={S.td}><Pill color="#c5a059">{e.event}</Pill></td>
            <td style={S.td}>{e.actor_id || "—"}</td>
            <td style={S.td}>{e.target_user_id || "—"}</td>
            <td style={S.td}>{e.old_tier || "—"} → {e.new_tier || "—"}</td>
            <td style={S.td} title={e.reason}>{(e.reason || "—").slice(0, 50)}</td>
            <td style={S.td}>{fmtDate(e.timestamp)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function UserMembershipTable({ users, onAction, onViewEntitlements }) {
  if (!users || !users.length) {
    return <p style={{ color: "#fcd34d", fontFamily: "'Space Mono', monospace", fontSize: 11 }} data-testid="users-empty">NO_DATA_AVAILABLE</p>;
  }
  return (
    <table style={S.table} data-testid="users-table">
      <thead>
        <tr>
          <th style={S.th}>EMAIL</th>
          <th style={S.th}>TIER</th>
          <th style={S.th}>STATUS</th>
          <th style={S.th}>ASSIGNED BY</th>
          <th style={S.th}>EXPIRES</th>
          <th style={S.th}>ACTIONS</th>
        </tr>
      </thead>
      <tbody>
        {users.map((u, i) => (
          <tr key={u.user_id || i} data-testid={`user-row-${u.user_id || i}`}>
            <td style={S.td}>
              <div style={{ color: "#fde68a", fontFamily: "'Cinzel', serif" }}>{u.email || "—"}</div>
              <div style={{ color: "#94a3b8", fontSize: 10, fontFamily: "'Space Mono', monospace" }}>{u.user_id}</div>
            </td>
            <td style={S.td}><Pill color={TIER_COLOR[u.current_tier] || "#94a3b8"}>{u.current_tier}</Pill></td>
            <td style={S.td}>{u.tier_status ? <Pill color={STATUS_COLOR[u.tier_status] || "#94a3b8"}>{u.tier_status}</Pill> : <span style={{ color: "#94a3b8" }}>—</span>}</td>
            <td style={S.td}>{u.tier_assigned_by || "—"}</td>
            <td style={S.td}>{fmtDate(u.tier_expires_at)}</td>
            <td style={S.td}>
              <div style={S.rowActions}>
                <button type="button" style={S.btnGhost} onClick={() => onAction("assign", u)}    data-testid={`action-assign-${u.user_id}`}>Assign</button>
                <button type="button" style={S.btnGhost} onClick={() => onAction("upgrade", u)}   data-testid={`action-upgrade-${u.user_id}`}>Upgrade</button>
                <button type="button" style={S.btnGhost} onClick={() => onAction("downgrade", u)} data-testid={`action-downgrade-${u.user_id}`}>Downgrade</button>
                <button type="button" style={S.btnGhost} onClick={() => onAction("trial", u)}     data-testid={`action-trial-${u.user_id}`}>Trial</button>
                <button type="button" style={S.btnGhost} onClick={() => onAction("comp", u)}      data-testid={`action-comp-${u.user_id}`}>Comp</button>
                <button type="button" style={S.btnGhost} onClick={() => onAction("revoke", u)}    data-testid={`action-revoke-${u.user_id}`}>Revoke</button>
                <button type="button" style={S.btnGhost} onClick={() => onViewEntitlements(u)}    data-testid={`action-entitlements-${u.user_id}`}>Entitlements</button>
              </div>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// ---------- Main page ----------

export default function AdminOneMembershipDashboard() {
  const [widgets, setWidgets]   = useState(null);
  const [users, setUsers]       = useState([]);
  const [search, setSearch]     = useState("");
  const [tierFilter, setTierFilter] = useState("");
  const [busy, setBusy]         = useState(false);
  const [modal, setModal]       = useState({ open: false, kind: "assign", user: null });
  const [toast, setToast]       = useState("");
  const [audit, setAudit]       = useState([]);
  const [entUser, setEntUser]   = useState(null);
  const [ent, setEnt]           = useState([]);
  const _once = useRef(false);

  const loadWidgets = useCallback(async () => {
    try { const r = await _req("/api/adminone/membership/widgets"); setWidgets(r); }
    catch (e) { setToast(`Widgets failed: ${e.message || e}`); }
  }, []);

  const loadUsers = useCallback(async () => {
    try {
      const qs = new URLSearchParams();
      if (search) qs.set("q", search);
      if (tierFilter) qs.set("tier", tierFilter);
      qs.set("limit", "100");
      const r = await _req(`/api/adminone/membership/users?${qs.toString()}`);
      setUsers(r.users || []);
    } catch (e) { setToast(`Users failed: ${e.message || e}`); }
  }, [search, tierFilter]);

  const loadAudit = useCallback(async () => {
    try { const r = await _req("/api/adminone/membership/audit?limit=50"); setAudit(r.events || []); }
    catch (e) { setToast(`Audit failed: ${e.message || e}`); }
  }, []);

  useEffect(() => {
    if (_once.current) return; _once.current = true;
    loadWidgets(); loadUsers(); loadAudit();
  }, [loadWidgets, loadUsers, loadAudit]);

  const openModal = useCallback((kind, user) => setModal({ open: true, kind, user }), []);
  const closeModal = useCallback(() => setModal({ open: false, kind: "assign", user: null }), []);

  const onViewEntitlements = useCallback(async (u) => {
    try {
      const r = await _req(`/api/adminone/membership/entitlements/${encodeURIComponent(u.user_id)}`);
      setEntUser(u); setEnt(r.entitlements || []);
    } catch (e) { setToast(`Entitlements failed: ${e.message || e}`); }
  }, []);

  const submit = useCallback(async ({ tier, reason, expires_at, trial_days }) => {
    setBusy(true);
    try {
      const u = modal.user || {};
      const base = { user_id: u.user_id, email: u.email, reason, effective_at: "now", expires_at: expires_at || null };
      let path = "/api/adminone/membership/assign";
      let body = { ...base, tier };
      if (modal.kind === "upgrade")   path = "/api/adminone/membership/upgrade";
      if (modal.kind === "downgrade") path = "/api/adminone/membership/downgrade";
      if (modal.kind === "revoke")    { path = "/api/adminone/membership/revoke"; body = { ...base, expires_at: undefined, reason }; }
      if (modal.kind === "comp")      path = "/api/adminone/membership/comp";
      if (modal.kind === "trial")     { path = "/api/adminone/membership/trial"; body = { ...base, tier, trial_days: trial_days || 14 }; }
      const r = await _req(path, { method: "POST", body: JSON.stringify(body) });
      setToast(`✓ ${modal.kind} → ${(r.assignment && r.assignment.new_tier) || tier}`);
      closeModal(); await loadUsers(); await loadAudit(); await loadWidgets();
    } catch (e) {
      setToast(`${modal.kind} failed: ${e.message || e}`);
    } finally { setBusy(false); }
  }, [modal, closeModal, loadUsers, loadAudit, loadWidgets]);

  const dist = (widgets && widgets.widgets && widgets.widgets.tier_distribution) || {};
  const kpis = useMemo(() => ([
    { k: "membership_users",       label: "MEMBERSHIP USERS",      v: (widgets && widgets.widgets && widgets.widgets.membership_users) ?? "—" },
    { k: "active_paid_tiers",      label: "ACTIVE PAID TIERS",     v: (widgets && widgets.widgets && widgets.widgets.active_paid_tiers) ?? "—" },
    { k: "manual_ceo_assignments", label: "MANUAL CEO ASSIGNMENTS",v: (widgets && widgets.widgets && widgets.widgets.manual_ceo_assignments) ?? "—" },
    { k: "trials_active",          label: "TRIALS ACTIVE",         v: (widgets && widgets.widgets && widgets.widgets.trials_active) ?? "—" },
    { k: "comped_accounts",        label: "COMPED ACCOUNTS",       v: (widgets && widgets.widgets && widgets.widgets.comped_accounts) ?? "—" },
    { k: "expiring_access",        label: "EXPIRING ACCESS (14D)", v: (widgets && widgets.widgets && widgets.widgets.expiring_access) ?? "—" },
    { k: "entitlement_errors",     label: "ENTITLEMENT ERRORS",    v: (widgets && widgets.widgets && widgets.widgets.entitlement_errors) ?? "—" },
    { k: "membership_audit_events",label: "AUDIT EVENTS",          v: (widgets && widgets.widgets && widgets.widgets.membership_audit_events) ?? "—" },
  ]), [widgets]);

  return (
    <main style={S.page} data-testid="adminone-membership-control-page">
      <header style={S.header}>
        <div>
          <div style={S.eyebrow}>ADMINONE · CEO MEMBERSHIP CONTROL™</div>
          <h1 style={S.title}>Membership Control — Assign, upgrade, revoke, audit</h1>
          <p style={S.sub}>
            All mutations are RBAC-enforced server-side. Every assignment is audit-logged with actor, target, old → new tier, reason,
            IP, and user-agent. Tiers unlock <strong>only built + tested + routed</strong> entitlements; pending features are tagged
            <code> FEATURE_AWAITING_INTEGRATION</code> so the UI never lies.
          </p>
        </div>
      </header>

      {/* KPI widgets */}
      <section style={S.grid} data-testid="membership-kpis">
        {kpis.map((k) => (
          <div key={k.k} style={S.kpi} data-testid={`kpi-${k.k.replace(/_/g, '-')}`}>
            <div style={S.kpiLabel}>{k.label}</div>
            <div style={S.kpiValue}>{k.v}</div>
          </div>
        ))}
      </section>

      {/* Tier distribution */}
      <section style={S.section} data-testid="tier-distribution">
        <h2 style={S.sectionTitle}>Tier distribution</h2>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 10 }}>
          {TIERS.map((t) => (
            <div key={t} style={{ ...S.kpi, padding: "10px 12px" }} data-testid={`dist-${t}`}>
              <div style={S.kpiLabel}><Pill color={TIER_COLOR[t]}>{t}</Pill></div>
              <div style={{ ...S.kpiValue, fontSize: 20, marginTop: 8 }}>{dist[t] ?? 0}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Users table */}
      <section style={S.section} data-testid="users-section">
        <h2 style={S.sectionTitle}>Members</h2>
        <div style={S.toolbar}>
          <input style={S.input} placeholder="Search by email / user_id / name" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="users-search" />
          <select style={S.select} value={tierFilter} onChange={(e) => setTierFilter(e.target.value)} data-testid="users-tier-filter">
            <option value="">All tiers</option>
            {TIERS.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
          <button type="button" style={S.btn} onClick={loadUsers} data-testid="users-reload">Reload</button>
        </div>
        <UserMembershipTable users={users} onAction={openModal} onViewEntitlements={onViewEntitlements} />
      </section>

      {/* Entitlement viewer */}
      {entUser && (
        <section style={S.section} data-testid="entitlement-section">
          <h2 style={S.sectionTitle}>Entitlements — {entUser.email || entUser.user_id}</h2>
          <MembershipEntitlementViewer entitlements={ent} />
        </section>
      )}

      {/* Audit panel */}
      <section style={S.section} data-testid="audit-section">
        <h2 style={S.sectionTitle}>Audit trail</h2>
        <MembershipAuditPanel events={audit} />
      </section>

      <div style={S.compliance} data-testid="membership-compliance">
        Truthfulness Mandate: features tagged FEATURE_AWAITING_INTEGRATION never unlock automatically when a tier is assigned manually.
        All mutations are server-enforced, audit-logged, and reason-required. Frontend-only tier changes are rejected by the backend.
      </div>

      <MembershipAssignmentModal
        open={modal.open}
        kind={modal.kind}
        user={modal.user}
        busy={busy}
        onClose={closeModal}
        onSubmit={submit}
      />

      {toast && (
        <div style={S.toast} role="status" data-testid="membership-toast" onClick={() => setToast("")}>{toast}</div>
      )}
    </main>
  );
}
